import { Injectable } from '@angular/core';
import { ParameterService, ParameterModel, ColorItemModel, Section } from './parameter.service';
import { BehaviorSubject, of, firstValueFrom, Observable } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
  export function themeInitializerFactory(themeService: ThemeService) {
    return () => themeService.initTheme();
  }
@Injectable({ providedIn: 'root' })
export class ThemeService {

  private readonly defaultLogo = 'assets/imtech-new-logo.svg';
  private readonly defaultFavicon = 'assets/favicon.ico';

  private logoSubject = new BehaviorSubject<string>(this.defaultLogo);
  logo$ = this.logoSubject.asObservable();

  private faviconSubject = new BehaviorSubject<string>(this.defaultFavicon);
  favicon$ = this.faviconSubject.asObservable();

  private parameterSubject = new BehaviorSubject<ParameterModel | null>(null);
  parameter$ = this.parameterSubject.asObservable();

  sections$: Observable<Section[]> = this.parameter$.pipe(
    filter(p => !!p), // ignore nulls
    map(param => param!.sections || [])
  );

  private defaultColors: ColorItemModel[] = [
    { variable: '--primary-color', value: '#D50000', name: 'Couleur principale', description: '', previewType: 'text' },
    { variable: '--standard-color', value: '#18181B', name: 'Couleur standard', description: '', previewType: 'text' },
    {
        name: 'Couleur standard secondaire',
        variable: '--secondary-standard-color',
        value: '#A8A29E',
        description: 'Texte et éléments standards (secondaire)',
        previewType: 'text'
    },
    { variable: '--primary-icon-color', value: '#D50000', name: 'Couleur icônes', description: '', previewType: 'icon' },
    { variable: '--primary-button-color', value: '#D50000', name: 'Couleur boutons', description: '', previewType: 'button' },
  ];

  private readonly localStorageKey = 'app_parameter';
  private readonly localStorageUpdatedKey = 'app_parameter_lastUpdated';

  constructor(private parameterService: ParameterService) {}

  /** Initialize theme before app bootstrap */
  /** Initialize theme before app bootstrap */
  async initTheme(): Promise<void> {
    const cachedParamJson = localStorage.getItem(this.localStorageKey);
    const cachedParam: ParameterModel | null = cachedParamJson ? JSON.parse(cachedParamJson) : null;
    const cachedUpdated = localStorage.getItem(this.localStorageUpdatedKey);

    try {
      // Step 1: fetch lastUpdated from server
      const serverUpdated = await firstValueFrom(
        this.parameterService.getLastUpdated().pipe(catchError(() => of('')))
      );

      let param: ParameterModel | null = cachedParam;

      // Step 2: fetch full parameter only if cache is missing or outdated
      if (!cachedUpdated || serverUpdated !== cachedUpdated) {
        param = await firstValueFrom(
          this.parameterService.getParameter().pipe(catchError(() => of(cachedParam)))
        );

        if (param) {
          localStorage.setItem(this.localStorageKey, JSON.stringify(param));
          if (serverUpdated) localStorage.setItem(this.localStorageUpdatedKey, serverUpdated);
        }
      }

      // Step 3: Apply theme (colors, logo, favicon) and update observable
      if (param) {
        this.applyColorsLogoAndFavicon(param);
        this.parameterSubject.next(param);
      }

    } catch (e) {
      console.error('Error loading parameter', e);
      if (cachedParam) {
        this.applyColorsLogoAndFavicon(cachedParam);
        this.parameterSubject.next(cachedParam);
      }
    }
  }


  private applyColorsLogoAndFavicon(param: ParameterModel) {
    const colors = param?.colors?.length ? param.colors : this.defaultColors;
    colors.forEach(color => document.documentElement.style.setProperty(color.variable, color.value));

    this.logoSubject.next(param?.logoUrl || this.defaultLogo);
    this.updateFavicon(param?.faviconUrl || this.defaultFavicon);
  }

  private updateFavicon(faviconUrl: string) {
    const link = document.querySelector<HTMLLinkElement>("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = faviconUrl;
    if (!link.parentElement) document.head.appendChild(link);
    this.faviconSubject.next(faviconUrl);
  }

  getCurrentLogo(): string { return this.logoSubject.value; }
  getCurrentFavicon(): string { return this.faviconSubject.value; }

  /** Returns the current parameter (from cache/localStorage) */
  getParameter(): ParameterModel | null { return this.parameterSubject.value; }

  /** Returns sections inside the parameter */
  getSections(): any[] { return this.getParameter()?.sections || []; }
}
